import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Chip, Field } from '@promtver/ui-kit';
import { SlotStatus } from '@promtver/contracts';

import {
  addBlackoutDate,
  getBlackoutDates,
  getPartnerSlots,
  getWeeklySchedule,
  removeBlackoutDate,
  saveWeeklySchedule,
  updatePartnerSlot,
} from '../../api/partner';
import { errorMessage } from '../../api/errors';
import { AppShell } from '../../components/AppShell';
import { AsyncState } from '../../components/AsyncState';
import { KeyValue } from '../../components/KeyValue';
import { ru } from '../../i18n/ru';
import { formatDateTimeLocal, formatDuration, todayInputValue } from '../../lib/datetime';
import type { BlackoutDate, WeeklySchedule } from '../../api/types';

const WEEK_DAYS = [
  { value: 1, label: ru.schedule.dayMon },
  { value: 2, label: ru.schedule.dayTue },
  { value: 3, label: ru.schedule.dayWed },
  { value: 4, label: ru.schedule.dayThu },
  { value: 5, label: ru.schedule.dayFri },
  { value: 6, label: ru.schedule.daySat },
  { value: 0, label: ru.schedule.daySun },
];

const DEFAULT_SCHEDULE: WeeklySchedule = {
  days: [1, 2, 3, 4, 5],
  startTime: '10:00',
  durationMin: 60,
  capacity: 15,
};

/**
 * Расписание предприятия (FR-MGR-02): слоты, дни недели, длительность,
 * вместимость, каникулы и дни технического обслуживания.
 * Изменения отправляются на сервер — инвалидация кэша каталога происходит там.
 */
export function SchedulePage() {
  const queryClient = useQueryClient();
  const [date, setDate] = useState(todayInputValue());
  const [weekly, setWeekly] = useState<WeeklySchedule>(DEFAULT_SCHEDULE);
  const [blackoutDate, setBlackoutDate] = useState(todayInputValue());
  const [blackoutReason, setBlackoutReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const slotsQuery = useQuery({
    queryKey: ['partner', 'slots', date],
    queryFn: () => getPartnerSlots(date),
  });

  const weeklyQuery = useQuery({
    queryKey: ['partner', 'weekly-schedule'],
    queryFn: getWeeklySchedule,
  });

  const blackoutQuery = useQuery({
    queryKey: ['partner', 'blackout-dates'],
    queryFn: getBlackoutDates,
  });

  useEffect(() => {
    if (weeklyQuery.data) {
      setWeekly(weeklyQuery.data);
    }
  }, [weeklyQuery.data]);

  const slotMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: SlotStatus }) => updatePartnerSlot(id, { status }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['partner', 'slots'] });
    },
  });

  const weeklyMutation = useMutation({
    mutationFn: (schedule: WeeklySchedule) => saveWeeklySchedule(schedule),
    onSuccess: async () => {
      setFormError(null);
      await queryClient.invalidateQueries({ queryKey: ['partner', 'weekly-schedule'] });
    },
    onError: (error) => setFormError(errorMessage(error, ru.common.unknownError)),
  });

  const blackoutMutation = useMutation({
    mutationFn: (item: BlackoutDate) => addBlackoutDate(item),
    onSuccess: async () => {
      setBlackoutReason('');
      await queryClient.invalidateQueries({ queryKey: ['partner', 'blackout-dates'] });
    },
  });

  const removeBlackoutMutation = useMutation({
    mutationFn: (value: string) => removeBlackoutDate(value),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['partner', 'blackout-dates'] });
    },
  });

  const slots = slotsQuery.data ?? [];
  const blackouts = blackoutQuery.data ?? [];

  const toggleDay = (day: number) => {
    setWeekly((previous) => {
      const days = previous.days.includes(day)
        ? previous.days.filter((value) => value !== day)
        : [...previous.days, day];
      return { ...previous, days };
    });
  };

  return (
    <AppShell title={ru.schedule.title} subtitle={ru.schedule.subtitle} active="schedule">
      <Card title={ru.schedule.date}>
        <Field
          label={ru.schedule.date}
          value={date}
          onChange={setDate}
          type="date"
          required
        />
      </Card>

      <Card title={ru.schedule.slotTitle}>
        <AsyncState
          isLoading={slotsQuery.isLoading}
          isError={slotsQuery.isError}
          isEmpty={slots.length === 0}
          emptyTitle={ru.schedule.noSlots}
          emptyDescription={ru.schedule.noSlotsDescription}
          onRetry={() => void slotsQuery.refetch()}
        >
          <ul className="flex flex-col gap-3">
            {slots.map((slot) => (
              <li key={slot.id} className="rounded-card border border-line p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <strong className="text-sm">
                    {slot.excursion?.title ?? ru.common.notSpecified}
                  </strong>
                  <Badge tone={slot.status === SlotStatus.Open ? 'success' : 'warning'}>
                    {slot.status === SlotStatus.Open
                      ? ru.schedule.statusOpen
                      : slot.status === SlotStatus.Cancelled
                        ? ru.schedule.statusCancelled
                        : ru.schedule.statusClosed}
                  </Badge>
                </div>
                <div className="flex flex-col gap-1">
                  <KeyValue
                    label={ru.common.time}
                    value={formatDateTimeLocal(slot.startsAt)}
                    mono
                  />
                  <KeyValue
                    label={ru.schedule.capacity}
                    value={`${slot.reserved} / ${slot.capacity}`}
                    mono
                  />
                  <KeyValue
                    label={ru.schedule.duration}
                    value={formatDuration(slot.excursion?.durationMin ?? null)}
                  />
                </div>
                <div className="mt-3">
                  {slot.status === SlotStatus.Open ? (
                    <Button
                      variant="outline"
                      tone="warning"
                      disabled={slotMutation.isPending}
                      onClick={() => slotMutation.mutate({ id: slot.id, status: SlotStatus.Closed })}
                    >
                      {ru.schedule.close}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      tone="success"
                      disabled={slotMutation.isPending}
                      onClick={() => slotMutation.mutate({ id: slot.id, status: SlotStatus.Open })}
                    >
                      {ru.schedule.open}
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </AsyncState>
      </Card>

      <Card title={ru.schedule.weeklyTitle}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            {WEEK_DAYS.map((day) => (
              <Chip
                key={day.value}
                active={weekly.days.includes(day.value)}
                onClick={() => toggleDay(day.value)}
              >
                {day.label}
              </Chip>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={ru.schedule.weeklyStart}
              value={weekly.startTime}
              onChange={(value) => setWeekly((previous) => ({ ...previous, startTime: value }))}
              type="time"
              required
            />
            <Field
              label={ru.schedule.weeklyDuration}
              value={String(weekly.durationMin)}
              onChange={(value) =>
                setWeekly((previous) => ({ ...previous, durationMin: Number(value) || 0 }))
              }
              inputMode="numeric"
              required
            />
            <Field
              label={ru.schedule.weeklyCapacity}
              value={String(weekly.capacity)}
              onChange={(value) =>
                setWeekly((previous) => ({ ...previous, capacity: Number(value) || 0 }))
              }
              inputMode="numeric"
              required
            />
          </div>

          {formError ? <p className="text-xs text-danger">{formError}</p> : null}

          <Button
            tone="accent"
            disabled={weeklyMutation.isPending}
            onClick={() => weeklyMutation.mutate(weekly)}
          >
            {weeklyMutation.isPending ? ru.common.saving : ru.common.save}
          </Button>
        </div>
      </Card>

      <Card title={ru.schedule.blackoutTitle}>
        <div className="flex flex-col gap-3">
          <Field
            label={ru.schedule.blackoutDate}
            value={blackoutDate}
            onChange={setBlackoutDate}
            type="date"
            required
          />
          <Field
            label={ru.schedule.blackoutReason}
            value={blackoutReason}
            onChange={setBlackoutReason}
            placeholder={ru.common.notSpecified}
          />
          <Button
            tone="accent"
            disabled={blackoutMutation.isPending || blackoutReason.trim().length === 0}
            onClick={() =>
              blackoutMutation.mutate({
                date: blackoutDate,
                reason: blackoutReason.trim(),
                kind: 'maintenance',
              })
            }
          >
            {ru.schedule.blackoutAdd}
          </Button>

          <AsyncState
            isLoading={blackoutQuery.isLoading}
            isError={blackoutQuery.isError}
            isEmpty={blackouts.length === 0}
            emptyTitle={ru.schedule.blackoutEmpty}
            onRetry={() => void blackoutQuery.refetch()}
          >
            <ul className="flex flex-col gap-2">
              {blackouts.map((item) => (
                <li
                  key={item.date}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-line p-3"
                >
                  <div className="flex flex-col">
                    <span className="font-mono text-sm">{item.date}</span>
                    <span className="text-xs text-muted">{item.reason}</span>
                  </div>
                  <Button
                    variant="outline"
                    tone="danger"
                    disabled={removeBlackoutMutation.isPending}
                    onClick={() => removeBlackoutMutation.mutate(item.date)}
                  >
                    {ru.common.delete}
                  </Button>
                </li>
              ))}
            </ul>
          </AsyncState>
        </div>
      </Card>
    </AppShell>
  );
}
